import React, { useEffect, useState } from 'react';
import { Phone, Mail, CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { OKRState, KPIPeriodicity } from '../types';
import { periodicityLabels } from '../helpers';
import { getActivityStats } from 'interfaces/okr';

// Must mirror defaultPeriodCount in toola-back/src/controller/okr/okrController.ts —
// how many rows a "page" holds, so pagination steps by a full page each time.
const PAGE_SIZE: Record<KPIPeriodicity, number> = { daily: 14, weekly: 8, monthly: 6, quarterly: 4 };

interface ActivityStatsRow {
    userId: string;
    calls: number;
    emails: number;
    meetings: number;
}

interface ActivityBucket {
    label: string;
    from: string;
    to: string;
    calls: number;
    emails: number;
    meetings: number;
}

const formatBucketLabel = (bucket: ActivityBucket, periodicity: KPIPeriodicity) => {
    const from = new Date(bucket.from);
    const to = new Date(bucket.to);

    if (periodicity === 'daily') {
        return from.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (periodicity === 'weekly') {
        return `${from.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} – ${to.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`;
    }
    if (periodicity === 'quarterly') {
        const quarter = Math.floor(from.getMonth() / 3) + 1;
        return `T${quarter} ${from.getFullYear()}`;
    }
    const label = from.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const ActivityStats: React.FC<{ state: OKRState }> = ({ state }) => {
    const [periodicity, setPeriodicity] = useState<KPIPeriodicity>('weekly');
    const [offset, setOffset] = useState(0);
    const [rows, setRows] = useState<ActivityStatsRow[]>([]);
    const [buckets, setBuckets] = useState<ActivityBucket[]>([]);
    const [totals, setTotals] = useState({ calls: 0, emails: 0, meetings: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getActivityStats(periodicity, offset).then((data) => {
            if (cancelled) return;
            setRows(data?.byUser ?? []);
            setBuckets(data?.buckets ?? []);
            setTotals(data?.totals ?? { calls: 0, emails: 0, meetings: 0 });
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [periodicity, offset]);

    const handlePeriodicityChange = (next: KPIPeriodicity) => {
        setPeriodicity(next);
        setOffset(0);
    };

    const memberName = (userId: string) =>
        state.team.find((m) => m.id === userId)?.name ?? 'Commercial inconnu';

    const rangeLabel = buckets.length > 0
        ? `${new Date(buckets[0].from).toLocaleDateString('fr-FR')} – ${new Date(buckets[buckets.length - 1].to).toLocaleDateString('fr-FR')}`
        : '';

    return (
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h3 className="font-bold text-navy-700">Activités commerciales</h3>
                    <p className="text-xs text-gray-400">Appels, emails et RDV logués depuis les fiches prospect/client, par commercial.</p>
                </div>
                <select
                    value={periodicity}
                    onChange={(e) => handlePeriodicityChange(e.target.value as KPIPeriodicity)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-toola-500"
                >
                    {(Object.entries(periodicityLabels) as [KPIPeriodicity, string][]).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="rounded-xl bg-blue-50 p-4 flex items-center gap-3">
                    <Phone size={18} className="text-toola-500" />
                    <div><p className="text-lg font-bold text-navy-700">{totals.calls}</p><p className="text-xs text-gray-500">Appels</p></div>
                </div>
                <div className="rounded-xl bg-blue-50 p-4 flex items-center gap-3">
                    <Mail size={18} className="text-toola-500" />
                    <div><p className="text-lg font-bold text-navy-700">{totals.emails}</p><p className="text-xs text-gray-500">Emails</p></div>
                </div>
                <div className="rounded-xl bg-blue-50 p-4 flex items-center gap-3">
                    <CalendarClock size={18} className="text-toola-500" />
                    <div><p className="text-lg font-bold text-navy-700">{totals.meetings}</p><p className="text-xs text-gray-500">RDV</p></div>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-gray-500">Détail par période</p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setOffset((o) => o + PAGE_SIZE[periodicity])}
                        className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
                        title="Période précédente"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{rangeLabel}</span>
                    <button
                        type="button"
                        onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE[periodicity]))}
                        disabled={offset === 0}
                        className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Période suivante"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                            <th className="py-2 font-medium">Période</th>
                            <th className="py-2 font-medium text-right">Appels</th>
                            <th className="py-2 font-medium text-right">Emails</th>
                            <th className="py-2 font-medium text-right">RDV</th>
                        </tr>
                    </thead>
                    <tbody>
                        {buckets.map((bucket) => (
                            <tr key={bucket.label} className="border-b border-gray-50 last:border-0">
                                <td className="py-2.5 text-navy-700 font-medium">{formatBucketLabel(bucket, periodicity)}</td>
                                <td className="py-2.5 text-right text-gray-600">{bucket.calls}</td>
                                <td className="py-2.5 text-right text-gray-600">{bucket.emails}</td>
                                <td className="py-2.5 text-right text-gray-600">{bucket.meetings}</td>
                            </tr>
                        ))}
                        {!loading && buckets.length === 0 && (
                            <tr><td colSpan={4} className="py-8 text-center text-gray-400">Aucune donnée sur la période affichée.</td></tr>
                        )}
                    </tbody>
                    {buckets.length > 0 && (
                        <tfoot>
                            <tr className="border-t border-gray-200 font-bold text-navy-700">
                                <td className="py-2.5">Total</td>
                                <td className="py-2.5 text-right">{totals.calls}</td>
                                <td className="py-2.5 text-right">{totals.emails}</td>
                                <td className="py-2.5 text-right">{totals.meetings}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <p className="text-xs font-semibold text-gray-500 mb-2">Par commercial</p>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                            <th className="py-2 font-medium">Commercial</th>
                            <th className="py-2 font-medium text-right">Appels</th>
                            <th className="py-2 font-medium text-right">Emails</th>
                            <th className="py-2 font-medium text-right">RDV</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.userId} className="border-b border-gray-50 last:border-0">
                                <td className="py-2.5 text-navy-700 font-medium">{memberName(row.userId)}</td>
                                <td className="py-2.5 text-right text-gray-600">{row.calls}</td>
                                <td className="py-2.5 text-right text-gray-600">{row.emails}</td>
                                <td className="py-2.5 text-right text-gray-600">{row.meetings}</td>
                            </tr>
                        ))}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={4} className="py-8 text-center text-gray-400">Aucune activité loguée sur la période affichée.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <p className="mt-4 text-xs text-gray-400">
                Astuce : utilisez ces compteurs comme source auto-sync (MetricBridge) lors de la création d'un KPI d'objectif — ex. « 50 appels ce mois-ci ».
            </p>
        </div>
    );
};

export default ActivityStats;
